import React, { useMemo, useState, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import {
  getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice,
} from '../components/helpers';
import {
  Page, Hero, StatGrid, Section, Segmented, Empty, Thumb,
  getItemTypes, findSkinImage, money, signed, pct, compactMoney,
} from './mobileShared';

// Report — the analytics story on one column. Every section keeps the desktop
// explanation line, because the numbers are only useful if you know which
// rows went into them.

const RANGES = [
  { key: 'all', label: 'All', days: null },
  { key: '90d', label: '90d', days: 90 },
  { key: '30d', label: '30d', days: 30 },
  { key: '7d',  label: '7d',  days: 7 },
];

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="mb-tip">
      <span className="l">{label}</span>
      <span className={`v ${v < 0 ? 'down' : 'up'}`}>{signed(v)}</span>
    </div>
  );
}

export default function MobileAnalytics({ rows, settings, livePrices, skinsData = [], zone }) {
  const [rangeKey, setRangeKey] = useState('all');
  const range = RANGES.find(r => r.key === rangeKey);

  // Literal colours: recharts writes these into SVG fill/stroke attributes.
  const profC = zone ? '#0d7a43' : (settings?.profitTextColor || '#4ade80');
  const lossC = zone ? '#c8134b' : (settings?.lossTextColor   || '#f87171');
  const ink   = zone ? '#131313' : '#9196aa';
  const grid  = zone ? 'rgba(19,19,19,0.16)' : 'rgba(255,255,255,0.07)';

  const isExcluded = useCallback(r => {
    const types = getItemTypes(r);
    const t = types.length ? settings?.itemTypes?.find(it => it.value === types[0].value) : null;
    return t?.overrideProfitLoss === true;
  }, [settings?.itemTypes]);

  const rangeSold = useMemo(() => {
    let sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (range.days) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - range.days);
      sold = sold.filter(r => new Date(r.timestamp) >= cutoff);
    }
    return sold;
  }, [rows, range, isExcluded]);

  const figures = useMemo(() => {
    const profit   = rangeSold.reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);
    const revenue  = rangeSold.reduce((s, r) => s + (parseFloat(r.sellPrice) || 0), 0);
    const soldCost = rangeSold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const wins     = rangeSold.filter(r => (parseFloat(r.profit) || 0) > 0).length;

    const unsold = getUnsoldRows(rows || []);
    const holdCost = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    let holdValue = 0, priced = 0;
    unsold.forEach(r => {
      const p = getLivePrice(livePrices, r)?.starting_at?.price;
      if (p != null) { holdValue += p; priced++; }
      else holdValue += parseFloat(r.buyPrice) || 0;
    });
    const totalInvested = (rows || []).filter(r => r.skin?.value)
      .reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);

    return {
      profit, revenue, soldCost, wins,
      winRate:   rangeSold.length ? Math.round((wins / rangeSold.length) * 100) : null,
      avgProfit: rangeSold.length ? profit / rangeSold.length : null,
      roi:       soldCost > 0 ? (profit / soldCost) * 100 : null,
      soldCount: rangeSold.length,
      lifetimeProfit: getTotalProfit(rows || [], isExcluded),
      unsoldCount: unsold.length, holdCost, holdValue,
      holdPnl: holdValue - holdCost, priced, totalInvested,
    };
  }, [rangeSold, rows, livePrices, isExcluded]);

  const cumulative = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded)
      .filter(r => r.skin?.value)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let cum = 0;
    return sold.map(r => {
      cum += parseFloat(r.profit) || 0;
      return {
        date: new Date(r.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        profit: parseFloat(cum.toFixed(2)),
      };
    });
  }, [rows, isExcluded]);

  const monthly = useMemo(() => {
    const byMonth = {};
    getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value).forEach(r => {
      const d = new Date(r.timestamp);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = {
        key, label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), profit: 0, count: 0,
      };
      byMonth[key].profit += parseFloat(r.profit) || 0;
      byMonth[key].count  += 1;
    });
    return Object.values(byMonth)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }))
      .slice(-12); // a phone axis fits about a year of labels
  }, [rows, isExcluded]);

  const bestMonth  = useMemo(() => monthly.reduce((b, m) => (!b || m.profit > b.profit ? m : b), null), [monthly]);
  const worstMonth = useMemo(() => monthly.reduce((w, m) => (!w || m.profit < w.profit ? m : w), null), [monthly]);

  const topFlips = useMemo(() => [...rangeSold]
    .sort((a, b) => (parseFloat(b.profit) || 0) - (parseFloat(a.profit) || 0))
    .slice(0, 5), [rangeSold]);

  const worstFlips = useMemo(() => [...rangeSold]
    .sort((a, b) => (parseFloat(a.profit) || 0) - (parseFloat(b.profit) || 0))
    .filter(r => (parseFloat(r.profit) || 0) < 0)
    .slice(0, 3), [rangeSold]);

  if (!rows?.length) {
    return <Page><Empty>No data to report on yet.</Empty></Page>;
  }

  return (
    <Page>
      <div className="mb-rangebar">
        <Segmented options={RANGES} value={rangeKey} onChange={setRangeKey} />
      </div>

      <Hero
        label={`Profit ${range.days ? `in the last ${range.days} days` : 'all time'}`}
        value={compactMoney(figures.profit)}
        tone={figures.profit >= 0 ? 'up' : 'down'}
        sub={figures.roi != null
          ? `${pct(figures.roi)} return on ${money(figures.soldCost)} of stock`
          : 'no sales in this range'}
        foot={`${figures.soldCount} sale${figures.soldCount !== 1 ? 's' : ''}${figures.winRate != null ? ` · ${figures.winRate}% went green` : ''}`}
      />

      <Section title="Sales in this range" note="Only rows with a sell price, dated inside the range you picked.">
        <StatGrid items={[
          { label: 'Revenue', value: money(figures.revenue), sub: 'total taken in' },
          { label: 'Cost of goods', value: money(figures.soldCost), sub: 'what those items cost' },
          { label: 'Avg per sale', value: figures.avgProfit != null ? signed(figures.avgProfit) : 'n/a',
            color: (figures.avgProfit ?? 0) >= 0 ? profC : lossC },
          { label: 'Win rate', value: figures.winRate != null ? `${figures.winRate}%` : 'n/a',
            sub: `${figures.wins} of ${figures.soldCount}` },
        ]} />
      </Section>

      <Section title="What you hold right now" note="Always current, whatever range is selected above.">
        <StatGrid items={[
          { label: 'Open positions', value: String(figures.unsoldCount), sub: `${figures.priced} have a live price` },
          { label: 'Cost to hold', value: money(figures.holdCost), sub: 'capital tied up' },
          { label: 'Market value', value: money(figures.holdValue), sub: 'if sold at ask today' },
          { label: 'Paper P/L', value: signed(figures.holdPnl), color: figures.holdPnl >= 0 ? profC : lossC,
            sub: figures.holdCost > 0 ? pct((figures.holdPnl / figures.holdCost) * 100) : 'n/a' },
        ]} />
      </Section>

      {cumulative.length > 1 && (
        <Section title="Profit over time" note="Every sale added up in order, all time.">
          <div className="mb-panel chart">
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={cumulative} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="mbCum" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={profC} stopOpacity={zone ? 0.5 : 0.32} />
                    <stop offset="100%" stopColor={profC} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: ink, fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis tick={{ fill: ink, fontSize: 10 }} tickLine={false} axisLine={false} width={46} />
                <ReferenceLine y={0} stroke={grid} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="profit" stroke={profC} strokeWidth={2} fill="url(#mbCum)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}

      {monthly.length > 0 && (
        <Section
          title="Month by month"
          note={bestMonth && worstMonth
            ? `Best ${bestMonth.label} (${signed(bestMonth.profit)}) · worst ${worstMonth.label} (${signed(worstMonth.profit)})`
            : 'Profit booked per calendar month.'}
        >
          <div className="mb-panel chart">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={monthly} margin={{ top: 6, right: 4, left: -22, bottom: 0 }}>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: ink, fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: ink, fontSize: 10 }} tickLine={false} axisLine={false} width={46} />
                <ReferenceLine y={0} stroke={grid} />
                <Tooltip cursor={{ fill: 'rgba(129,140,248,0.10)' }} content={<Tip />} />
                <Bar dataKey="profit" radius={zone ? 0 : [4, 4, 0, 0]}>
                  {monthly.map((m, i) => <Cell key={i} fill={m.profit >= 0 ? profC : lossC} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}

      {topFlips.length > 0 && (
        <Section title="Best flips in range" note="Sorted by realized profit after fees.">
          <div className="mb-list">
            {topFlips.map((r, i) => {
              const p = parseFloat(r.profit) || 0;
              const buy = parseFloat(r.buyPrice) || 0;
              return (
                <div key={i} className="mb-row">
                  <span className="mb-rank">{i + 1}</span>
                  <Thumb src={findSkinImage(skinsData, r.skin.value, r.condition?.value)} size="sm" />
                  <div className="mb-row-main">
                    <div className="mb-row-name">{r.skin.value}</div>
                    <div className="mb-row-meta">
                      <span>{r.condition?.value || 'n/a'}</span>
                      <span>{money(buy)} → {money(r.sellPrice)}</span>
                    </div>
                  </div>
                  <div className="mb-row-stub">
                    <span className="v" style={{ color: p >= 0 ? profC : lossC }}>{signed(p)}</span>
                    {buy > 0 && <span className="p" style={{ color: p >= 0 ? profC : lossC }}>{pct((p / buy) * 100)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {worstFlips.length > 0 && (
        <Section title="Where you lost money" note="The losing sales in this range, worst first.">
          <div className="mb-list">
            {worstFlips.map((r, i) => {
              const p = parseFloat(r.profit) || 0;
              return (
                <div key={i} className="mb-row">
                  <Thumb src={findSkinImage(skinsData, r.skin.value, r.condition?.value)} size="sm" />
                  <div className="mb-row-main">
                    <div className="mb-row-name">{r.skin.value}</div>
                    <div className="mb-row-meta">
                      <span>{money(r.buyPrice)} → {money(r.sellPrice)}</span>
                    </div>
                  </div>
                  <div className="mb-row-stub">
                    <span className="v" style={{ color: lossC }}>{signed(p)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <Section title="Lifetime" note="Everything, ignoring the range filter.">
        <StatGrid items={[
          { label: 'Lifetime profit', value: signed(figures.lifetimeProfit),
            color: figures.lifetimeProfit >= 0 ? profC : lossC },
          { label: 'Total ever invested', value: money(figures.totalInvested), sub: 'sold and held combined' },
        ]} />
      </Section>
    </Page>
  );
}
